import { useQueryClient, useMutation } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import useSession from '@hooks/useSession'
import useContacts from '@hooks/useContacts'
import type { Contact } from '@services/contribution/contribution'
import {
  createContactForParticipation,
  bulkCreateContactsForParticipation,
  type CreateContactArgs,
  type BulkCreateContactData,
} from '@services/contribution/api'
import { toPossessive, isDefined } from '@/util/helpers'
import { InvalidSessionError } from '@/util/errors'
import { sendBulkContactUploadEvent, sendContactAddedEvent, sendContactAddErrorEvent } from '@/services/analytics/gtm'

import CloudSponge, { type CloudSpongeContact } from '@components/CloudSponge'
import Section from '@components/Section'
import PrizeProgressBar from '@/components/ProgressBars/PrizeProgressBar'
import InvitationList from '@components/InvitationList'
import AddContactForm from '@components/AddContactForm'
import Tooltip from '@components/Tooltip'
import { MAX_BULK_IMPORT_CONTACTS } from '@components/CloudSponge'
import LoadingScreen from '@/components/LoadingScreen'

function parseCloudSpongeContacts(
  cloudSpongeContacts: CloudSpongeContact[],
  participationId: string,
  defaultLanguage: 'en' | 'es' = 'en',
): BulkCreateContactData {
  const contacts = cloudSpongeContacts
    .map((contact) => {
      const name = contact.fullName() || ''
      const email = contact.primaryEmail() || ''
      const phone = contact.primaryPhone() || ''

      // Skip contacts without a name and (email or phone is missing)
      if (!name && (!email || !phone)) {
        return undefined
      }

      return {
        name: name.trim(),
        alias: '',
        email: email.trim(),
        phone: phone.trim(),
        language: defaultLanguage,
      }
    })
    .filter(isDefined)

  return {
    participationId,
    contacts,
  }
}

function formatContactError(errorMessage: string): string {
  if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('already been used')) {
    return 'This email address has already been used. Please use a different email.'
  }

  if (errorMessage.toLowerCase().includes('phone number already present')) {
    return 'This phone number has already been used. Please use a different phone number.'
  }

  if (errorMessage.toLowerCase().includes('participation not found')) {
    return 'The participation was not found. Please contact support if this issue persists.'
  }

  return errorMessage || 'Sorry! Something went wrong with your invitation. Please try again.'
}

export default function InvitationPage() {
  const { t } = useTranslation()
  const { contacts, contactGoal, isLoading } = useContacts()

  const queryClient = useQueryClient()
  const { session } = useSession()

  const createContact = useMutation({
    mutationFn: (contactData: CreateContactArgs) => createContactForParticipation(contactData),
    onSuccess: (data) => {
      if (data.errors && data.errors.length > 0) {
        data.errors.forEach((error) => {
          const errorMessages = error.errors || []
          errorMessages.forEach((errorMessage) => {
            Sentry.logger.error('Error creating contact', { error: errorMessage })
            const formattedMessage = formatContactError(errorMessage)
            toast.error(formattedMessage, {
              autoClose: false,
            })
          })
        })
      } else {
        toast.success(t('content.components.bulk_import.invitation_sent_toast'))
      }
      if (data.contacts) {
        queryClient.setQueryData(['contacts'], () => {
          return data
        })
      }
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
    onError: (error) => {
      Sentry.logger.error('Error creating contact', { error: String(error) })
      const errorMessage = error instanceof Error ? error.message : String(error)
      const formattedMessage = formatContactError(errorMessage)
      toast.error(formattedMessage, {
        autoClose: false,
      })
      return false
    },
  })

  const bulkCreateContacts = useMutation({
    mutationFn: (contactData: BulkCreateContactData) => bulkCreateContactsForParticipation(contactData),
    onSuccess: (data, { source }) => {
      const errorCount = data.errors?.length || 0

      // Get existing contacts to calculate how many are actually new
      const oldData = queryClient.getQueryData<{ contactGoal: number; contacts: Contact[] }>(['contacts'])
      const existingContactIds = oldData ? new Set(oldData.contacts.map((c) => c.id)) : new Set<number>()
      const newContacts = data.contacts.filter((c) => !existingContactIds.has(c.id))
      const successfulCount = newContacts.length

      if (data.errors && data.errors.length > 0) {
        data.errors.forEach((error) => {
          const errorMessages = error.errors || []
          const contactName = error.contact.name || 'Unknown contact'
          errorMessages.forEach((errorMessage) => {
            Sentry.logger.error(Sentry.logger.fmt`Error creating contact ${contactName}`, { error: errorMessage })
            const formattedMessage = formatContactError(errorMessage)
            // Don't show error if the contact already exists
            if (!formattedMessage.includes('already been used')) {
              toast.error(`${contactName}: ${formattedMessage}`, {
                autoClose: false,
              })
            }
          })
        })
      }

      sendBulkContactUploadEvent({
        isOnboarding: false,
        source: source ?? 'unknown',
        bulkUploadCount: successfulCount,
        errorCount: errorCount,
        contactCount: contacts.length + successfulCount,
      })

      if (successfulCount > 0) {
        if (errorCount === 0) {
          toast.success(t('content.components.bulk_import.import_success_toast', { count: successfulCount }))
        } else {
          toast.success(
            t('content.components.bulk_import.import_partial_success_toast', {
              count: successfulCount,
              errorCount,
            }),
          )
        }
      }

      queryClient.setQueryData(['contacts'], (oldData: { contactGoal: number; contacts: Contact[] } | undefined) => {
        if (!oldData) {
          return {
            contactGoal: data.contactGoal,
            contacts: data.contacts,
          }
        }

        const existingContactIds = new Set(oldData.contacts.map((c) => c.id))
        const newContacts = data.contacts.filter((c) => !existingContactIds.has(c.id))

        return {
          contactGoal: data.contactGoal,
          contacts: [...oldData.contacts, ...newContacts],
        }
      })

      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
    onError: (error) => {
      Sentry.logger.error('Error bulk creating contacts', { error: String(error) })
      const errorMessage = error instanceof Error ? error.message : String(error)
      const formattedMessage = formatContactError(errorMessage)
      toast.error(t('content.components.bulk_import.import_failure_toast', { error: formattedMessage }), {
        autoClose: false,
      })
    },
  })

  const handleSubmit = async (contact: {
    name: string
    phone: string
    email: string
    alias: string
    language: 'en' | 'es'
  }) => {
    if (!session || !session.participationId) {
      throw new InvalidSessionError('Cannot create contact without a valid session.')
    }

    try {
      const result = await createContact.mutateAsync({
        participationId: session.participationId,
        name: contact.name.trim(),
        email: contact.email.trim(),
        alias: contact.alias.trim(),
        phone: contact.phone.trim(),
        language: contact.language,
      })
      if (result.errors && result.errors.length > 0) {
        sendContactAddErrorEvent({ isOnboarding: false })
        return false
      }
      sendContactAddedEvent({
        isOnboarding: false,
        hasEmail: contact.email.trim().length > 0,
        hasPhone: contact.phone.trim().length > 0,
        contactCount: (contacts?.length ?? 0) + 1,
      })
      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  const handleImportContacts = (cloudSpongeContacts: CloudSpongeContact[], source: string) => {
    if (!cloudSpongeContacts || cloudSpongeContacts.length === 0) {
      toast.warning(t('content.components.bulk_import.no_contacts_selected_toast'), {
        autoClose: false,
      })
      return
    }
    if (!session || !session.participationId) {
      throw new InvalidSessionError('Cannot bulk import contacts without a valid session.')
    }

    let contactsToProcess = cloudSpongeContacts
    if (cloudSpongeContacts.length > MAX_BULK_IMPORT_CONTACTS) {
      toast.warning(
        t('content.components.bulk_import.over_limit_warning_toast', {
          count: cloudSpongeContacts.length,
          max: MAX_BULK_IMPORT_CONTACTS,
        }),
        {
          autoClose: false,
        },
      )

      contactsToProcess = cloudSpongeContacts.slice(0, MAX_BULK_IMPORT_CONTACTS)
    }

    const parsedContacts = parseCloudSpongeContacts(contactsToProcess, session.participationId, 'en')

    parsedContacts.source = source

    if (parsedContacts.contacts.length === 0) {
      toast.error(t('content.components.bulk_import.no_valid_contacts_toast'), {
        autoClose: false,
      })
      return
    }
    bulkCreateContacts.mutate(parsedContacts)
  }

  if (!session || isLoading) {
    return <LoadingScreen />
  }

  return (
    <>
      <title>{t('content.invite_page.title_tag')}</title>
      <div className='flex flex-col'>
        <div className='bg-cta text-center px-4 md:px-8'>
          <h1 className='text-white text-3xl font-serif text-center font-bold'>
            {t('content.invite_page.title', { school_name: toPossessive(session.schoolName) })}
          </h1>
          <div className='text-white pt-2'>{t('content.invite_page.subtitle', { goal: contactGoal ?? 10 })}</div>
        </div>

        <Section wrapperStyle='bg-white border-none mt-8 py-4 px-12'>
          <PrizeProgressBar />
        </Section>

        <div className='mb-8'>
          <h2 className='text-white text-2xl font-serif font-semibold text-center pt-16'>
            {t('content.components.input_contact.title')}
          </h2>
          <AddContactForm
            onSubmit={handleSubmit}
            labelStyle='!text-cta-txt'
            aliasTooltip={<Tooltip content={t('content.components.input_contact.nickname_tooltip')} variant='light' />}
          />
        </div>

        <CloudSponge onImportContacts={handleImportContacts} />

        <Section wrapperStyle='mt-16 mb-4'>
          <InvitationList />
          <PrizeProgressBar />
          <div className={`text-xs opacity-80 text-secondary-txt mt-8 pt-12 pb-6 border-t-1 border-pending`}>
            {t('content.components.input_contact.sms_consent_disclaimer')}
          </div>
        </Section>
      </div>
    </>
  )
}
